/**
 * MetaDashboard Component
 * Main dashboard showing Meta Pixel analytics
 */

import React, { useState } from 'react';
import axios from 'axios';
import { useAuthToken } from '../../hooks/useAuthToken';
import useMetaData from '../../hooks/useMetaData';
import EventSummary from './EventSummary';
import { useToast } from '../analytics/Toast';
import './MetaDashboard.css';

const MetaDashboard = ({ metaInfo, onDisconnect }) => {
  const [days, setDays] = useState(30);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Access token state
  const [accessToken, setAccessToken] = useState('');
  const [isSavingToken, setIsSavingToken] = useState(false);
  const [showTokenInput, setShowTokenInput] = useState(false);

  const { token } = useAuthToken();
  const { events, purchases, isLoading, hasAccessToken, refetch } = useMetaData(days);
  const { showToast } = useToast();

  const handleSaveToken = async () => {
    if (!accessToken.trim()) {
      showToast('Please enter an access token', 'error');
      return;
    }
    const businessId = localStorage.getItem('user_id');
    if (!businessId) {
      showToast('Business ID not found', 'error');
      return;
    }
    try {
      setIsSavingToken(true);
      const response = await axios.post(
        `${process.env.REACT_APP_APIURL}meta/access-token/save`,
        { business_id: businessId, accessToken: accessToken.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        showToast('Access token saved! Loading insights...', 'success');
        setAccessToken('');
        setShowTokenInput(false);
        if (refetch) refetch();
      } else {
        showToast(response.data.message || 'Failed to save token', 'error');
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || 'Failed to save access token',
        'error'
      );
    } finally {
      setIsSavingToken(false);
    }
  };

  const handleDisconnect = async () => {
    const businessId = localStorage.getItem('user_id');
    if (!businessId) {
      showToast('Business ID not found', 'error');
      return;
    }
    try {
      setIsDisconnecting(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_APIURL}meta/pixel/remove`,
        {
          data: { business_id: businessId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        showToast('Meta Pixel disconnected', 'success');
        setTimeout(() => {
          if (onDisconnect) onDisconnect();
        }, 500);
      } else {
        showToast(response.data.message || 'Failed to disconnect', 'error');
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || 'Failed to disconnect pixel',
        'error'
      );
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  return (
    <div className="meta-dashboard-wrapper">
      {/* Top Bar */}
      <div className="meta-dashboard-topbar">
        <div className="meta-dashboard-header">
          <div className="meta-brand-icons">
            {/* Facebook */}
            <div className="meta-brand-icon meta-brand-icon-fb">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.994 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            {/* Instagram */}
            <div className="meta-brand-icon meta-brand-icon-ig">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="#fff" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </div>
          </div>
          <div className="meta-dashboard-title-block">
            <h1 className="meta-dashboard-title">Meta Pixel Analytics</h1>
            <p className="meta-dashboard-platforms">Facebook & Instagram</p>
            <div className="meta-dashboard-subtitle">
              <span className="meta-dashboard-pixel-id">{metaInfo?.pixelId}</span>
              <span className="meta-dashboard-badge">Active</span>
            </div>
          </div>
        </div>

        <div className="meta-dashboard-controls">
          <div className="meta-date-pills">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                className={`meta-date-pill ${days === d ? 'meta-date-active' : ''}`}
                onClick={() => setDays(d)}
              >
                {d} days
              </button>
            ))}
          </div>
          <span
            className="meta-disconnect-btn"
            onClick={() => setShowDisconnectConfirm(true)}
          >
            × Disconnect
          </span>
        </div>
      </div>

      {/* Disconnect Confirmation */}
      {showDisconnectConfirm && (
        <div className="meta-disconnect-confirm-bar">
          <span>Remove Meta Pixel from your store?</span>
          <button
            className="meta-btn-confirm-yes"
            onClick={handleDisconnect}
            disabled={isDisconnecting}
          >
            {isDisconnecting ? 'Removing...' : 'Yes, remove'}
          </button>
          <button
            className="meta-btn-confirm-cancel"
            onClick={() => setShowDisconnectConfirm(false)}
            disabled={isDisconnecting}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Summary Cards (shown only if hasAccessToken) */}
      {hasAccessToken && purchases && (
        <div className="meta-summary-cards">
          <div className="meta-summary-card">
            <p className="meta-summary-label">Purchases</p>
            <h3 className="meta-summary-value">
              {purchases.purchasesFormatted || purchases.purchases || 0}
            </h3>
            <p className="meta-summary-sub">Last {days} days</p>
          </div>
          <div className="meta-summary-card">
            <p className="meta-summary-label">Purchase Value</p>
            <h3 className="meta-summary-value">
              {purchases.purchaseValueFormatted || '₹0'}
            </h3>
            <p className="meta-summary-sub">Total revenue</p>
          </div>
          <div className="meta-summary-card">
            <p className="meta-summary-label">Pixel Status</p>
            <h3 className="meta-summary-value" style={{ color: 'var(--meta-green)' }}>
              Active
            </h3>
            <p className="meta-summary-sub">Tracking events</p>
          </div>
        </div>
      )}

      {/* Events Section */}
      <div className="meta-events-section">
        <h2 className="meta-section-heading">Pixel events — last {days} days</h2>
        <EventSummary
          events={events}
          isLoading={isLoading}
          hasAccessToken={hasAccessToken}
        />
      </div>

      {/* Access Token Card — shown when no token saved yet */}
      {!hasAccessToken && (
        <div className="meta-token-card">
          <div className="meta-token-card-header">
            <div className="meta-token-card-icon">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
              </svg>
            </div>
            <div>
              <h3 className="meta-token-card-title">Connect Meta Access Token</h3>
              <p className="meta-token-card-subtitle">
                Required to view PageView counts, purchases, and event insights
              </p>
            </div>
          </div>

          {!showTokenInput ? (
            <button
              className="meta-token-add-btn"
              onClick={() => setShowTokenInput(true)}
            >
              + Add Access Token
            </button>
          ) : (
            <div className="meta-token-input-area">
              <div className="meta-token-how-to">
                <p className="meta-token-how-title">How to get your access token:</p>
                <ol className="meta-token-steps">
                  <li>Go to <a href="https://business.facebook.com/latest/settings/system_users" target="_blank" rel="noopener noreferrer">Meta Business Settings → System Users</a></li>
                  <li>Click your System User → <strong>Generate token</strong></li>
                  <li>Select your app, choose <strong>ads_read</strong> + <strong>business_management</strong></li>
                  <li>Copy the token and paste below</li>
                </ol>
              </div>
              <textarea
                className="meta-token-input"
                placeholder="Paste your Meta access token here (EAABsb...)"
                value={accessToken}
                onChange={e => setAccessToken(e.target.value)}
                rows={3}
              />
              <div className="meta-token-actions">
                <button
                  className="meta-token-save-btn"
                  onClick={handleSaveToken}
                  disabled={isSavingToken || !accessToken.trim()}
                >
                  {isSavingToken ? (
                    <><span className="meta-spinner-sm" /> Saving...</>
                  ) : (
                    'Save Token'
                  )}
                </button>
                <button
                  className="meta-token-cancel-btn"
                  onClick={() => { setShowTokenInput(false); setAccessToken(''); }}
                  disabled={isSavingToken}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer Note */}
      <div className="meta-dashboard-footer">
        <svg viewBox="0 0 24 24">
          <path fill="currentColor" d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" />
        </svg>
        <span>
          Events appear in{' '}
          <a
            href="https://business.facebook.com/events_manager"
            target="_blank"
            rel="noopener noreferrer"
          >
            Meta Events Manager
          </a>{' '}
          within a few minutes
        </span>
      </div>
    </div>
  );
};

export default MetaDashboard;